import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import ReactJson from "@uiw/react-json-view";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Page from "../components/Page";
import Popup from "../components/Popup";
import {
  useBusiness,
  useDeleteBusinessMutation,
  useGetBasicInfoMutation,
  useGetEinvoiceDetailsMutation,
  useGetEinvoicePurchasesDataMutation,
  useGetEinvoiceSalesDataMutation,
  useGetGstr1DataMutation,
  useGetGstr2aDataMutation,
  useGetGstr2bDataMutation,
  useRefreshBusinessMutation,
} from "../data/business";

import { downloadInstructionsMD, downloadOpenAPIYAML } from "../data/business";

export default function Business() {
  const { gstin } = useParams();
  const data = useBusiness(gstin || "");
  const navigate = useNavigate();

  const [response, setResponse] = useState<any>(null);
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [openDataFetchPopup, setOpenDataFetchPopup] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [paramName, setParamName] = useState("");
  const [queryFn, setQueryFn] = useState<() => void>(() => {});

  const refreshBusinessMutation = useRefreshBusinessMutation();
  const deleteBusinessMutation = useDeleteBusinessMutation();
  const getBasicInfoMutation = useGetBasicInfoMutation();
  const getGstr1DataMutation = useGetGstr1DataMutation();
  const getGstr2aDataMutation = useGetGstr2aDataMutation();
  const getGstr2bDataMutation = useGetGstr2bDataMutation();
  const getEinvoicePurchasesDataMutation =
    useGetEinvoicePurchasesDataMutation();
  const getEinvoiceSalesDataMutation = useGetEinvoiceSalesDataMutation();
  const getEinvoiceDetailsMutation = useGetEinvoiceDetailsMutation();

  return (
    <Page title={`Business: ${data?.gstin}`}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>GSTIN</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Token Expires At</TableCell>
              <TableCell>Data Expires At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{data?.gstin}</TableCell>
              <TableCell>{data?.status}</TableCell>
              <TableCell>{data?.createdAt}</TableCell>
              <TableCell>{data?.tokenExpiresAt}</TableCell>
              <TableCell>{data?.dataExpiresAt}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("..")}
        >
          Back
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            refreshBusinessMutation.mutate(
              { gstin: gstin || "" },
              { onSuccess: (data) => setResponse(data) },
            )
          }
        >
          Refresh
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            getBasicInfoMutation.mutate(
              { gstin: gstin || "" },
              { onSuccess: (data) => setResponse(data) },
            )
          }
        >
          Basic
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch GSTR-1 Data");
            setParamName("Period");
            setQueryFn(() => (period: string) => {
              return getGstr1DataMutation.mutate(
                { gstin: gstin || "", period: period },
                { onSuccess: (data) => setResponse(data) },
              );
            });
            setOpenDataFetchPopup(true);
          }}
        >
          GSTR-1
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch GSTR-2A Data");
            setParamName("Period");
            setQueryFn(() => (period: string) => {
              return getGstr2aDataMutation.mutate(
                { gstin: gstin || "", period: period },
                { onSuccess: (data) => setResponse(data) },
              );
            });
            setOpenDataFetchPopup(true);
          }}
        >
          GSTR-2A
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch GSTR-2B Data");
            setParamName("Period");
            setQueryFn(() => (period: string) => {
              return getGstr2bDataMutation.mutate(
                { gstin: gstin || "", period: period },
                { onSuccess: (data) => setResponse(data) },
              );
            });
            setOpenDataFetchPopup(true);
          }}
        >
          GSTR-2B
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch Purchases Data");
            setParamName("Period");
            setQueryFn(() => (period: string) => {
              return getEinvoicePurchasesDataMutation.mutate(
                { gstin: gstin || "", period: period },
                { onSuccess: (data) => setResponse(data) },
              );
            });
            setOpenDataFetchPopup(true);
          }}
        >
          Purchases
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch Sales Data");
            setParamName("Period");
            setQueryFn(() => (period: string) => {
              return getEinvoiceSalesDataMutation.mutate(
                { gstin: gstin || "", period: period },
                { onSuccess: (data) => setResponse(data) },
              );
            });
            setOpenDataFetchPopup(true);
          }}
        >
          Sales
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setPopupTitle("Fetch IRN Details");
            setParamName("IRN");
            setQueryFn(
              () => (irn: string) =>
                getEinvoiceDetailsMutation.mutate(
                  { gstin: gstin || "", irn: irn },
                  { onSuccess: (data) => setResponse(data) },
                ),
            );
            setOpenDataFetchPopup(true);
          }}
        >
          IRN Details
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            downloadOpenAPIYAML({ gstin: gstin || "" });
          }}
        >
          OpenAPI YAML
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            downloadInstructionsMD({ gstin: gstin || "" });
          }}
        >
          Instructions
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeletePopup(true);
          }}
        >
          Delete
        </Button>
      </Box>

      <Divider sx={{ my: 2 }} />

      {response && (
        <Paper sx={{ padding: 2 }}>
          <ReactJson
            value={response}
            collapsed={1}
            style={{
              padding: 2,
            }}
          />
        </Paper>
      )}

      <Popup
        action={() => {
          deleteBusinessMutation.mutate(
            { gstin: gstin || "" },
            { onSuccess: (data) => setResponse(data) },
          );
        }}
        actionText="Yes"
        title="Delete Business"
        content="Are you sure you want to delete this business?"
        open={openDeletePopup}
        setOpen={setOpenDeletePopup}
      />

      <DataFetchPopup
        open={openDataFetchPopup}
        setOpen={setOpenDataFetchPopup}
        title={popupTitle}
        paramName={paramName}
        queryFn={queryFn}
      />
    </Page>
  );
}

function DataFetchPopup({
  open,
  setOpen,
  title,
  paramName,
  queryFn,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  paramName: string;
  queryFn: (paramValue: string) => void;
}) {
  const [paramValue, setParamValue] = useState("");

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          <TextField
            label={paramName}
            type="text"
            value={paramValue}
            onChange={(e) => setParamValue(e.target.value)}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            queryFn(paramValue);
            setParamValue("");
            setOpen(false);
          }}
        >
          Fetch
        </Button>
      </DialogActions>
    </Dialog>
  );
}
